/**
 * Single source of truth for per-category metadata.
 *
 * Every property here replaces a previously-scattered hardcoded constant:
 *   label          ← CATEGORY_LABELS dict
 *   partnerLabel   ← etikett för ett ej namngivet alternativ. Aldrig «partner»: Arvo har inga
 *                    leverantörspartner (neutralitetsmoaten, KM-05).
 *   segment        ← CATEGORY_TO_CLUSTER dict (ArvoScore)
 *   unit/unitSingular ← isMobil/isSaas terminology switches
 *   inlineLabel    ← multi-case inline label switch in analysis text
 *   isRealPrice    ← REAL_PRICE_CATEGORIES Set (show supplier name in partner block)
 *   benchmarkType  ← NEW: 'list-verified' | 'negotiated-target'
 *   benchmarkNote  ← NEW: specific disclaimer shown for negotiated-target categories
 *   variableChargeNote ← nested ternary switch for rörliga avgifter description
 *   licensePending ← insurance categories gated by FI-licence
 */

import { UTAN_VERIFIERAT_PRIS } from './diagnos.js';

export const CATEGORY_META = {
  mobil: {
    label:              'Mobilabonnemang',
    partnerLabel:       'Kvalificerad Mobiloperatör',
    segment:            2,
    unit:               'abonnemang',
    unitSingular:       'abonnemang',
    inlineLabel:        'mobilabonnemang',
    isRealPrice:        true,
    benchmarkType:      'list-verified',
    benchmarkNote:      null,
    variableChargeNote: 'Roaming, övertrafik m.m. — ej inkluderat i årsberäkningen.',
    licensePending:     false,
    elSuffix:           false,
  },
  molnvaxel: {
    label:              'Företagsväxel (molnväxel)',
    partnerLabel:       'Kvalificerad Växeloperatör',
    segment:            2,
    unit:               'användare',
    unitSingular:       'användare',
    inlineLabel:        'molnväxel',
    isRealPrice:        true,
    benchmarkType:      'list-verified',
    benchmarkNote:      'Verifierat instegspris: Telia Smart Connect från 89 kr/anv/mån exkl moms (telia.se). Exakt pris beror på nivå och tillval.',
    variableChargeNote: 'Samtalsavgifter och tillval utöver licensen — ej inkluderat i årsberäkningen.',
    licensePending:     false,
    elSuffix:           false,
  },
  bredband: {
    label:              'Företagsbredband',
    partnerLabel:       'Kvalificerad Bredbandsoperatör',
    segment:            2,
    unit:               'anslutningar',
    unitSingular:       'anslutning',
    inlineLabel:        'bredband',
    isRealPrice:        true,
    benchmarkType:      'list-verified',
    benchmarkNote:      'Verifierat mot leverantörens publika listpris (Tele2 address-API) — exakt pris beror på adress och befintlig infrastruktur.',
    variableChargeNote: 'Datatrafik och överskottsavgifter — ej inkluderat i årsberäkningen.',
    licensePending:     false,
    elSuffix:           false,
  },
  'saas-productivity': {
    label:              'Programvarulicenser / SaaS',
    partnerLabel:       'Kvalificerad SaaS-leverantör',
    segment:            3,
    unit:               'licenser',
    unitSingular:       'licens',
    inlineLabel:        'programvarulicenser',
    isRealPrice:        true,
    benchmarkType:      'list-verified',
    benchmarkNote:      null,
    variableChargeNote: null,
    licensePending:     false,
    elSuffix:           false,
  },
  'saas-crm': {
    label:              'CRM-system',
    partnerLabel:       'Kvalificerad CRM-leverantör',
    segment:            3,
    unit:               'licenser',
    unitSingular:       'licens',
    inlineLabel:        'programvarulicenser',
    isRealPrice:        false,
    benchmarkType:      'list-verified',
    benchmarkNote:      null,
    variableChargeNote: null,
    licensePending:     false,
    elSuffix:           false,
  },
  'saas-finance': {
    label:              'Affärssystem / Bokföring',
    partnerLabel:       'Kvalificerad Affärssystemsleverantör',
    segment:            3,
    unit:               'licenser',
    unitSingular:       'licens',
    inlineLabel:        'programvarulicenser',
    isRealPrice:        false,
    benchmarkType:      'list-verified',
    benchmarkNote:      null,
    variableChargeNote: null,
    licensePending:     false,
    elSuffix:           false,
  },
  'saas-other': {
    label:              'Programvarulicenser / SaaS · övrigt',
    partnerLabel:       'Kvalificerad SaaS-leverantör',
    segment:            3,
    unit:               'licenser',
    unitSingular:       'licens',
    inlineLabel:        'programvarulicenser',
    isRealPrice:        false,
    benchmarkType:      'list-verified',
    benchmarkNote:      null,
    variableChargeNote: null,
    licensePending:     false,
    elSuffix:           false,
  },
  'saas-creative': {
    label:              'Kreativ mjukvara / Design',
    partnerLabel:       'Kvalificerad Mjukvaruleverantör',
    segment:            3,
    unit:               'licenser',
    unitSingular:       'licens',
    inlineLabel:        'programvarulicenser',
    isRealPrice:        false,
    benchmarkType:      'list-verified',
    benchmarkNote:      null,
    variableChargeNote: null,
    licensePending:     false,
    elSuffix:           false,
  },
  el: {
    label:              'Elavtal',
    partnerLabel:       'Kvalificerad Elleverantör',
    segment:            1,
    unit:               'avtal',
    unitSingular:       'avtal',
    inlineLabel:        'el (energidel)',
    isRealPrice:        false,
    benchmarkType:      'list-verified',
    benchmarkNote:      null,
    variableChargeNote: 'Rörliga energikostnader (spotpris, nätavgift) — ej inkluderat i årsberäkningen.',
    licensePending:     false,
    elSuffix:           true,
  },
  skrivarleasing: {
    label:              'Skrivare & Managed Print',
    partnerLabel:       'Kvalificerad Print-leverantör',
    segment:            0,
    unit:               'enheter',
    unitSingular:       'enhet',
    inlineLabel:        'skrivarlösning',
    isRealPrice:        false,
    benchmarkType:      'negotiated-target',
    benchmarkNote:      UTAN_VERIFIERAT_PRIS,
    variableChargeNote: 'Klickkostnader per utskrift (volymbaserat) — ej inkluderat i årsberäkningen.',
    licensePending:     false,
    elSuffix:           false,
  },
  utrustningsleasing: {
    label:              'IT-utrustningsleasing',
    partnerLabel:       'Kvalificerad IT-leverantör',
    segment:            0,
    unit:               'enheter',
    unitSingular:       'enhet',
    inlineLabel:        'utrustningsleasing',
    isRealPrice:        false,
    benchmarkType:      'negotiated-target',
    benchmarkNote:      UTAN_VERIFIERAT_PRIS,
    variableChargeNote: null,
    licensePending:     false,
    elSuffix:           false,
  },
  kortterminal: {
    label:              'Kortterminal',
    partnerLabel:       'Kvalificerad Betaltjänstleverantör',
    segment:            6,
    unit:               'terminaler',
    unitSingular:       'terminal',
    inlineLabel:        'kortterminal',
    isRealPrice:        false,
    benchmarkType:      'list-verified',
    benchmarkNote:      null,
    variableChargeNote: 'Transaktionsavgifter och volymbaserade procentavgifter — ej inkluderat i årsberäkningen.',
    licensePending:     false,
    elSuffix:           false,
  },
  'faktura-tjanst': {
    label:              'Fakturatjänst / Affärssystem',
    partnerLabel:       'Kvalificerad Affärssystemsleverantör',
    segment:            6,
    unit:               'licenser',
    unitSingular:       'licens',
    inlineLabel:        'fakturatjänst',
    isRealPrice:        false,
    benchmarkType:      'list-verified',
    benchmarkNote:      null,
    variableChargeNote: null,
    licensePending:     false,
    elSuffix:           false,
  },
  'leasing-bil': {
    label:              'Företagsleasing',
    partnerLabel:       'Kvalificerad leasingleverantör',
    segment:            5,
    unit:               'fordon',
    unitSingular:       'fordon',
    inlineLabel:        'billeasing',
    isRealPrice:        false,
    benchmarkType:      'negotiated-target',
    benchmarkNote:      UTAN_VERIFIERAT_PRIS,
    variableChargeNote: null,
    licensePending:     false,
    elSuffix:           false,
  },
  'it-support': {
    label:              'IT-drift & Support',
    partnerLabel:       'Kvalificerad IT-leverantör',
    segment:            4,
    unit:               'avtal',
    unitSingular:       'avtal',
    inlineLabel:        'IT-support',
    isRealPrice:        false,
    benchmarkType:      'negotiated-target',
    benchmarkNote:      UTAN_VERIFIERAT_PRIS,
    variableChargeNote: null,
    licensePending:     false,
    elSuffix:           false,
  },
  serverhosting: {
    label:              'Serverhosting & Cloud-infrastruktur',
    partnerLabel:       'Kvalificerad IT-leverantör',
    segment:            4,
    unit:               'avtal',
    unitSingular:       'avtal',
    inlineLabel:        'serverhosting',
    isRealPrice:        false,
    benchmarkType:      'negotiated-target',
    benchmarkNote:      UTAN_VERIFIERAT_PRIS,
    variableChargeNote: null,
    licensePending:     false,
    elSuffix:           false,
  },
  'transport-frakt': {
    label:              'Transport & Frakt',
    partnerLabel:       'Kvalificerad Fraktleverantör',
    segment:            5,
    unit:               'avtal',
    unitSingular:       'avtal',
    inlineLabel:        'transport',
    isRealPrice:        false,
    benchmarkType:      'negotiated-target',
    benchmarkNote:      UTAN_VERIFIERAT_PRIS,
    variableChargeNote: null,
    licensePending:     false,
    elSuffix:           false,
  },
  kontorsmaterial: {
    label:              'Kontorsmaterial & Förbrukning',
    partnerLabel:       'Kvalificerad Förbrukningsleverantör',
    segment:            6,
    unit:               'avtal',
    unitSingular:       'avtal',
    inlineLabel:        'kontorsmaterial',
    isRealPrice:        false,
    benchmarkType:      'negotiated-target',
    benchmarkNote:      UTAN_VERIFIERAT_PRIS,
    variableChargeNote: null,
    licensePending:     false,
    elSuffix:           false,
  },
  'städ-rengöring': {
    label:              'Städ & Rengöring',
    partnerLabel:       'Kvalificerad Städleverantör',
    segment:            6,
    unit:               'avtal',
    unitSingular:       'avtal',
    inlineLabel:        'städtjänst',
    isRealPrice:        false,
    benchmarkType:      'negotiated-target',
    benchmarkNote:      UTAN_VERIFIERAT_PRIS,
    variableChargeNote: null,
    licensePending:     false,
    elSuffix:           false,
  },
  'larm-bevakning': {
    label:              'Larm & Bevakning',
    partnerLabel:       'Kvalificerad Säkerhetsleverantör',
    segment:            6,
    unit:               'avtal',
    unitSingular:       'avtal',
    inlineLabel:        'larm och bevakning',
    isRealPrice:        false,
    benchmarkType:      'negotiated-target',
    benchmarkNote:      UTAN_VERIFIERAT_PRIS,
    variableChargeNote: null,
    licensePending:     false,
    elSuffix:           false,
  },
  foretagshalsovard: {
    label:              'Företagshälsovård',
    partnerLabel:       'Kvalificerad hälsovårdsleverantör',
    segment:            7,
    unit:               'avtal',
    unitSingular:       'avtal',
    inlineLabel:        'företagshälsovård',
    isRealPrice:        false,
    benchmarkType:      'negotiated-target',
    benchmarkNote:      UTAN_VERIFIERAT_PRIS,
    variableChargeNote: null,
    licensePending:     false,
    elSuffix:           false,
  },
  loneadmin: {
    label:              'Löneadministration',
    partnerLabel:       'Kvalificerad Lönesystemleverantör',
    segment:            7,
    unit:               'anställda',
    unitSingular:       'anställd',
    inlineLabel:        'löneadministration',
    isRealPrice:        true,
    benchmarkType:      'list-verified',
    benchmarkNote:      'Verifierat golv: Fortnox Lön 199 kr/mån + 25 kr/anställd/mån exkl moms (fortnox.se). Exakt utfall beror på om behovet ryms i Fortnox Lön.',
    variableChargeNote: 'Lönebesked-/utskicksavgifter (Kivra) är rörliga — ej inkluderat i golvjämförelsen.',
    licensePending:     false,
    elSuffix:           false,
  },
  'forsakring-foretag': {
    label:              'Företagsförsäkring',
    partnerLabel:       'Kvalificerat försäkringsbolag',
    segment:            7,
    unit:               'avtal',
    unitSingular:       'avtal',
    inlineLabel:        'företagsförsäkring',
    isRealPrice:        false,
    benchmarkType:      'negotiated-target',
    benchmarkNote:      UTAN_VERIFIERAT_PRIS,
    variableChargeNote: null,
    licensePending:     true,
    elSuffix:           false,
  },
  'forsakring-ansvar': {
    label:              'Yrkesansvarsförsäkring',
    partnerLabel:       'Kvalificerat försäkringsbolag',
    segment:            7,
    unit:               'avtal',
    unitSingular:       'avtal',
    inlineLabel:        'yrkesansvarsförsäkring',
    isRealPrice:        false,
    benchmarkType:      'negotiated-target',
    benchmarkNote:      UTAN_VERIFIERAT_PRIS,
    variableChargeNote: null,
    licensePending:     true,
    elSuffix:           false,
  },
  bankavgifter: {
    label:              'Bankavgifter & Betaltjänster',
    partnerLabel:       'Kvalificerad bank',
    segment:            6,
    unit:               'avtal',
    unitSingular:       'avtal',
    inlineLabel:        'bankavgifter',
    isRealPrice:        false,
    benchmarkType:      'negotiated-target',
    benchmarkNote:      UTAN_VERIFIERAT_PRIS,
    variableChargeNote: null,
    licensePending:     false,
    elSuffix:           false,
  },
  'avfall-atervinning': {
    label:              'Avfall & Återvinning',
    partnerLabel:       'Kvalificerad Avfallsleverantör',
    segment:            6,
    unit:               'avtal',
    unitSingular:       'avtal',
    inlineLabel:        'avfall och återvinning',
    isRealPrice:        false,
    benchmarkType:      'negotiated-target',
    benchmarkNote:      UTAN_VERIFIERAT_PRIS,
    variableChargeNote: null,
    licensePending:     false,
    elSuffix:           false,
  },
  uncategorized: {
    label:              'Okategoriserad',
    partnerLabel:       'Kvalificerad leverantör',
    segment:            0,
    unit:               'enheter',
    unitSingular:       'enhet',
    inlineLabel:        'denna tjänst',
    isRealPrice:        false,
    benchmarkType:      'negotiated-target',
    benchmarkNote:      UTAN_VERIFIERAT_PRIS,
    variableChargeNote: null,
    licensePending:     false,
    elSuffix:           false,
  },
};

export function getCategoryMeta(category) {
  return CATEGORY_META[category] ?? {
    label:              category ?? 'Okänd kategori',
    partnerLabel:       'Kvalificerad leverantör',
    segment:            0,
    unit:               'enheter',
    unitSingular:       'enhet',
    inlineLabel:        'denna tjänst',
    isRealPrice:        false,
    benchmarkType:      'negotiated-target',
    benchmarkNote:      UTAN_VERIFIERAT_PRIS,
    variableChargeNote: null,
    licensePending:     false,
    elSuffix:           false,
  };
}
